import { ArrowClockwise, CheckCircle, Lock, Spinner, WarningCircle } from "@phosphor-icons/react";

/**
 * Loading, empty, failed and refused, drawn the way the sketches of 2026-09-24 draw them: an icon, a
 * title and at most one line, inside the box the list would have filled.
 *
 * The same four kinds and the same test ids as `app/components/StateBlock.tsx`, which the client's
 * screens keep until the second build round. The one rule carried over unchanged is the important
 * one: a failure offers a retry and a refusal never does, because a retry for something the server
 * will always refuse teaches that the portal is unreliable rather than that the access is missing.
 */
export type BoxStateKind = "loading" | "empty" | "error" | "denied";

const ICON = {
  loading: Spinner,
  empty: CheckCircle,
  error: WarningCircle,
  denied: Lock
} as const;

export function BoxState({
  kind,
  title,
  body,
  compact = false,
  onRetry
}: {
  kind: BoxStateKind;
  title: string;
  body?: string;
  compact?: boolean;
  onRetry?: () => void;
}) {
  const Icon = ICON[kind];
  const role = kind === "error" ? "alert" : "status";

  return (
    <div
      className={`state${compact ? " is-compact" : ""}`}
      role={role}
      aria-live={role === "alert" ? "assertive" : "polite"}
      data-testid={`state-${kind}`}
    >
      <span
        className={`type-icon${kind === "error" ? " tone-danger" : ""}${kind === "loading" ? " is-spinning" : ""}`}
        aria-hidden="true"
      >
        <Icon size={20} />
      </span>
      <h3 className="state-title">{title}</h3>
      {body ? <p>{body}</p> : null}
      {onRetry && kind === "error" ? (
        <button type="button" className="button" onClick={onRetry}>
          <ArrowClockwise size={16} aria-hidden="true" />
          <span>Försök igen</span>
        </button>
      ) : null}
    </div>
  );
}
